import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Sparkles, Shield, Award, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { fetchCollection } from '../../lib/cms';
import Beregner from '../../components/Beregner';
import GalleryModal from '../../components/GalleryModal';
import SEO from '../../components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function HomeDe() {
  const [testimonials, setTestimonials] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [loadVideo, setLoadVideo] = useState(true);

  useEffect(() => {
    async function loadData() {
      const [testData, galleriData] = await Promise.all([
        fetchCollection('testimonials', 'de'),
        fetchCollection('galleri', 'de')
      ]);
      setTestimonials(testData);
      if (galleriData && galleriData.length > 0) {
        setGalleryImages(galleriData[0].images || []);
      }
    }
    loadData();
  }, []);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleCount(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCount(2);
      } else {
        setVisibleCount(3);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, testimonials.length - visibleCount);

  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [visibleCount, maxIndex, currentIndex]);

  useEffect(() => {
    if (testimonials.length === 0 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials, isPaused, maxIndex]);

  const nextSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const openGallery = (index) => {
    setGalleryIndex(index);
    setIsGalleryOpen(true);
  };

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Danshaker',
    url: 'https://danshaker.dk/de',
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-50 text-slate-900 antialiased">
      <SEO
        title="Halbieren Sie die Stallarbeit"
        description="Danshaker ist die originale dänische Entmistungs- und Sortiermaschine. Bis zu 50% Zeitersparnis, halber Einstreuverbrauch und körperliche Entlastung. Jetzt kostenlose Demo anfordern."
        path="/de"
        lang="de"
        schema={homeSchema}
      />

      {/* Decorative background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />
      <div className="pointer-events-none absolute top-[30%] left-[-10%] h-[600px] w-[600px] rounded-full bg-[#0E67A2]/2 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-[10%] right-[-10%] h-[600px] w-[600px] rounded-full bg-[#0E67A2]/2 blur-[120px]" />

      {/* Hero Section */}
      <section className="relative mx-auto max-w-7xl px-4 pt-8 pb-10 md:px-8 md:pt-12 lg:pt-16 md:pb-14 lg:pb-16">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
          {/* Left Content */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="space-y-8 lg:col-span-6"
          >
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-2.5 rounded-full border border-[#0E67A2]/20 bg-[#0E67A2]/5 px-5 py-2 text-sm font-bold text-[#0E67A2]"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              <span>Die originale dänische Entmistungsmaschine</span>
            </motion.div>

            <div className="space-y-4">
              <motion.h1 variants={fadeInUp} className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl md:text-6xl lg:text-7xl leading-none">
                Halbieren Sie die Arbeit<br />
                <span className="bg-gradient-to-r from-[#0E67A2] to-sky-600 bg-clip-text text-transparent">im Pferdestall!</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="max-w-2xl text-lg md:text-xl text-slate-800 leading-relaxed font-medium">
                Der Danshaker wurde entwickelt, um die schwere und monotone Entmistungsarbeit spürbar zu erleichtern. Sparen Sie bis zu 50% Ihrer Zeit, halbieren Sie den Einstreuverbrauch und schonen Sie Ihren Körper.
              </motion.p>
            </div>

            {/* CTA Buttons */}
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Link
                to="/de/kostenlose-demo"
                className="flex items-center gap-2.5 rounded-xl bg-[#0E67A2] px-6 py-3.5 text-base font-bold text-white shadow-lg shadow-[#0E67A2]/10 transition duration-300 hover:scale-[1.02] hover:bg-[#0E67A2]/90"
              >
                <Calendar className="h-5 w-5" aria-hidden="true" />
                Kostenlose Demo buchen
              </Link>
              <Link
                to="/de/produkt"
                className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-base font-bold text-slate-700 shadow-sm transition duration-300 hover:scale-[1.02] hover:bg-slate-50"
              >
                Produkt ansehen
              </Link>
              <Link
                to="/de/kontakt"
                className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-base font-bold text-slate-700 shadow-sm transition duration-300 hover:scale-[1.02] hover:bg-slate-50"
              >
                Kontakt aufnehmen
              </Link>
            </motion.div>

            {/* Trust Badges */}
            <motion.div variants={fadeInUp} className="grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-slate-200 pt-10 text-slate-500">
              <div className="flex items-center gap-3">
                <Shield className="h-6 w-6 text-[#0E67A2] shrink-0" aria-hidden="true" />
                <span className="text-sm font-semibold text-slate-700">Dänisches Design & Fertigung</span>
              </div>
              <div className="flex items-center gap-3">
                <Award className="h-6 w-6 text-[#0E67A2] shrink-0" aria-hidden="true" />
                <span className="text-sm font-semibold text-slate-700">Hochwertige Werkstoffe</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-6 w-6 text-[#0E67A2] shrink-0" aria-hidden="true" />
                <span className="text-sm font-semibold text-slate-700">Zuverlässige Lieferung</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Video / Visual */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="relative lg:col-span-6"
          >
            <div className="relative mx-auto max-w-[750px] lg:max-w-none">
              <div className="absolute -inset-6 rounded-3xl bg-[#0E67A2]/5 blur-3xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-4 shadow-xl">
                {loadVideo && (
                  <video
                    src="https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/dd23b170-9451-48c1-be2e-929bf23525fb.mov"
                    poster="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/82c2389c-2509-456f-8e9c-aa5c44a15f3e.jpg"
                    className="w-full h-auto max-h-[600px] mx-auto rounded-2xl shadow-2xl bg-black"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                  />
                )}
                <div className="absolute bottom-6 right-6 rounded-xl bg-white/95 px-4 py-2 text-xs font-bold text-slate-900 shadow-lg border border-slate-200">
                  Danshaker in Aktion sehen
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="border-y border-slate-200 bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-black text-slate-950 md:text-4xl">Warum ein Danshaker?</h2>
            <p className="mt-4 text-base text-slate-900 font-semibold">
              Der Danshaker optimiert Ihr Arbeitsumfeld im Stall und senkt Ihre Betriebskosten nachhaltig.
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {/* Zeit sparen */}
            <motion.div variants={fadeInUp} className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-8 text-center shadow-md transition hover:border-[#0E67A2]/40 hover:shadow-lg">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0E67A2]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=85,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/a792de19-260d-4ca8-9514-ca552cf7c662.png"
                  alt="Zeit sparen"
                  width="40"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-10 object-contain"
                />
              </div>
              <h3 className="text-xl font-black text-slate-950 mb-2">ZEIT sparen</h3>
              <p className="text-[#0E67A2] font-black mb-4">Bis zu 50% schnellere Entmistung</p>
              <p className="text-sm text-slate-900 font-semibold leading-relaxed">
                Danshaker hat die Arbeitszeit bei vielen unserer Kunden um über die Hälfte reduziert. Mehr Zeit für die Pferde, weniger Plackerei.
              </p>
            </motion.div>

            {/* Einstreu sparen */}
            <motion.div variants={fadeInUp} className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-8 text-center shadow-md transition hover:border-[#0E67A2]/40 hover:shadow-lg">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0E67A2]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=85,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/feb939a4-8f35-4c2d-a66d-e8556b06f45a.png"
                  alt="Einstreu sparen"
                  width="40"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-10 object-contain"
                />
              </div>
              <h3 className="text-xl font-black text-slate-950 mb-2">EINSTREU sparen</h3>
              <p className="text-[#0E67A2] font-black mb-4">Bis zu 50% weniger Verbrauch</p>
              <p className="text-sm text-slate-900 font-semibold leading-relaxed">
                Trennen Sie Einstreu in wenigen Sekunden und behalten Sie das saubere Material in der Box. Nur der Mist kommt auf den Haufen.
              </p>
            </motion.div>

            {/* Körper schonen */}
            <motion.div variants={fadeInUp} className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-8 text-center shadow-md transition hover:border-[#0E67A2]/40 hover:shadow-lg md:col-span-2 lg:col-span-1">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0E67A2]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=85,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/17efd1be-1500-4bfa-a02d-0792317ba244.png"
                  alt="Körper schonen"
                  width="40"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-10 object-contain"
                />
              </div>
              <h3 className="text-xl font-black text-slate-950 mb-2">KÖRPER schonen</h3>
              <p className="text-[#0E67A2] font-black mb-4">Vermeiden Sie monotone Überlastung</p>
              <p className="text-sm text-slate-900 font-semibold leading-relaxed">
                Schluss mit repetitivem manuellem Rütteln der Mistgabel. Schonen Sie Rücken, Schultern und Handgelenke dauerhaft.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Feature Walkthrough */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-bold text-[#0E67A2]">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Einfache und intuitive Bedienung</span>
              </div>
              <h2 className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
                So funktioniert der Danshaker
              </h2>
              <p className="text-lg text-slate-700 font-medium leading-relaxed">
                Schaufeln Sie das Material aus der Pferdebox einfach auf das Rüttelsieb der Maschine. Der Danshaker trennt in Sekundenschnelle die Pferdeäpfel von der sauberen Einstreu.
              </p>
              <div className="space-y-4 pt-2">
                <div className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0E67A2] font-bold text-white text-sm">
                    1
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Einstreu aufschaufeln</h3>
                    <p className="text-sm text-slate-600 font-medium">Bequemes Beladen ohne schweres Heben.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0E67A2] font-bold text-white text-sm">
                    2
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Automatische Trennung</h3>
                    <p className="text-sm text-slate-600 font-medium">Die saubere Einstreu fällt zurück, Mist verbleibt im Auffangbehälter.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0E67A2] font-bold text-white text-sm">
                    3
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Reine Einstreu wiederverwenden</h3>
                    <p className="text-sm text-slate-600 font-medium">Lockerer, elastischer und gesunder Boxenboden für Ihr Pferd.</p>
                  </div>
                </div>
              </div>
              <div className="pt-4">
                <Link
                  to="/de/produkt"
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-6 py-3.5 font-bold text-white transition hover:bg-slate-800 shadow-md"
                >
                  Alle Spezifikationen ansehen
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-3 shadow-xl">
                <img
                  src="https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/danshaker-3-farver.png"
                  alt="Danshaker Farben und Ausführungen"
                  width="700"
                  height="450"
                  loading="lazy"
                  decoding="async"
                  className="w-full rounded-2xl object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Beregner Section */}
      <section className="border-t border-slate-300 bg-slate-100 py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">Wie viel können Sie sparen?</h2>
            <p className="mt-4 text-slate-700 font-medium">
              Nutzen Sie unseren interaktiven Ersparnisrechner und sehen Sie, wie schnell sich ein Danshaker in Ihrem Betrieb bezahlt macht.
            </p>
          </div>

          <Beregner lang="de" />
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="border-t border-slate-300 py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">Was unsere Kunden sagen</h2>
              <p className="mt-4 text-slate-700 font-medium">
                Erfahrungen von professionellen Reitbetrieben und Pferdehaltern, die den Danshaker täglich einsetzen.
              </p>
            </div>

            <div 
              className="relative overflow-hidden"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div 
                className="flex flex-nowrap transition-transform duration-500 ease-in-out gap-6"
                style={{ 
                  transform: `translateX(calc(-${currentIndex} * (100% + 1.5rem) / ${visibleCount}))` 
                }}
              >
                {testimonials.map((item) => (
                  <div 
                    key={item.id} 
                    className="flex flex-col justify-between rounded-2xl border border-slate-300 bg-white p-6 shadow-md transition hover:border-[#0E67A2]/20 w-full shrink-0 md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
                  >
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        {item.image && item.image[0] ? (
                          <img
                            src={item.image[0]}
                            alt={item.name}
                            width="48"
                            height="48"
                            loading="lazy"
                            decoding="async"
                            className="h-12 w-12 rounded-full object-cover border border-slate-300"
                          />
                        ) : (
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#0E67A2]/10 text-[#0E67A2] font-extrabold text-lg border border-[#0E67A2]/20">
                            {item.name ? item.name.charAt(0) : 'K'}
                          </div>
                        )}
                        <div>
                          <h3 className="font-bold text-slate-900 text-base">{item.name}</h3>
                          <p className="text-xs text-slate-700 font-semibold">{item.company}</p>
                          <div className="flex items-center gap-1 text-amber-400 text-xs mt-0.5" aria-label="5 von 5 Sternen">
                            <i className="fa-solid fa-star" />
                            <i className="fa-solid fa-star" />
                            <i className="fa-solid fa-star" />
                            <i className="fa-solid fa-star" />
                            <i className="fa-solid fa-star" />
                          </div>
                        </div>
                      </div>
                      <div
                        className="text-sm text-slate-800 leading-relaxed italic font-medium"
                        dangerouslySetInnerHTML={{ __html: item.text }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {maxIndex > 0 && (
              <div className="flex items-center justify-center gap-6 mt-10">
                <button
                  onClick={prevSlide}
                  className="p-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-[#0E67A2] transition-colors shadow-sm cursor-pointer"
                  aria-label="Vorherige Bewertung"
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>

                <div className="flex gap-2">
                  {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                        idx === currentIndex ? 'w-8 bg-[#0E67A2]' : 'w-2.5 bg-slate-200 hover:bg-slate-300'
                      }`}
                      aria-label={`Gehe zu Bewertung ${idx + 1}`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextSlide}
                  className="p-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-[#0E67A2] transition-colors shadow-sm cursor-pointer"
                  aria-label="Nächste Bewertung"
                >
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            )}

            <div className="mt-12 text-center">
              <Link
                to="/de/kundenstimmen"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 font-bold text-[#0E67A2] shadow-sm transition hover:bg-slate-50"
              >
                Alle Kundenstimmen lesen
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="border-t border-slate-300 py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">Erleben Sie den Danshaker im Detail</h2>
              <p className="mt-4 text-slate-700 font-medium">
                Klicken Sie auf die Bilder, um Details der Maschine und des Zubehörs in Großansicht zu betrachten.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
              {galleryImages.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => openGallery(idx)}
                  className="group relative aspect-square cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:border-[#0E67A2]/30"
                >
                  <img
                    src={img}
                    alt={`Danshaker Detail ${idx + 1}`}
                    width="300"
                    height="300"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-slate-950/20 opacity-0 transition group-hover:opacity-100 flex items-center justify-center">
                    <span className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#0E67A2] border border-slate-200 shadow-md">Vergrößern</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA Section */}
      <section className="border-t border-slate-300 bg-gradient-to-b from-white to-slate-100 py-12">
        <div className="mx-auto max-w-5xl px-4 text-center md:px-8">
          <h2 className="text-3xl font-extrabold text-slate-900 md:text-5xl leading-tight">
            Bereit, die tägliche Stallarbeit<br />
            <span className="bg-gradient-to-r from-[#0E67A2] to-sky-600 bg-clip-text text-transparent">zu revolutionieren?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate-800 font-medium">
            Buchen Sie eine unverbindliche und kostenlose Demo direkt in Ihrem Stall. Wir zeigen Ihnen genau, wie die Maschine arbeitet, und Sie dürfen sie selbst ausprobieren.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/de/kostenlose-demo"
              className="flex items-center gap-2 rounded-xl bg-[#0E67A2] px-8 py-4 font-bold text-white shadow-lg shadow-[#0E67A2]/20 transition hover:bg-[#0E67A2]/90"
            >
              <Calendar className="h-5 w-5" aria-hidden="true" />
              Jetzt kostenlose Demo buchen
            </Link>
            <Link
              to="/de/kontakt"
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-8 py-4 font-bold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Kontakt aufnehmen
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <GalleryModal
        isOpen={isGalleryOpen}
        images={galleryImages}
        currentIndex={galleryIndex}
        onClose={() => setIsGalleryOpen(false)}
        onPrev={() => setGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
        onNext={() => setGalleryIndex((prev) => (prev + 1) % galleryImages.length)}
      />
    </div>
  );
}
